import { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../store/reducers';
import { Actions } from '../store/actions';
import fetchPtSa, { PastSa } from '../api/fetchPtSa';
import { AssessmentSummary } from './prescriptionPad/AssessmentSummaryRightPanel';
import { SA } from '../api/fetchAssessmentSummary';
import fetchPastAssessmentSummary from '../api/fetchPastAssessmentSummary';

interface Props {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    plId: string;
}

type State = {
    selectedSaId?: string;
    fetchState: 'loading' | 'success' | 'error';
    saList: PastSa[];
    previewSaFetchState: 'loading' | 'success' | 'error';
    assessmentSummary?: SA;
};

class SelfAssessmentRightPanel extends PureComponent<Props, State> {
    state: State = {
        fetchState: 'loading',
        saList: [],
        previewSaFetchState: 'loading',
    };

    componentDidMount = async (): Promise<void> => {
        this.fetchDeets();
    };

    fetchDeets = () => {
        fetchPtSa(this.props.plId)
            .then(async (res) => {
                if (!res.success) {
                    this.setState({
                        fetchState: 'error',
                    });
                    return;
                }

                if (res.data && res.data.length > 0) {
                    this.setState({
                        saList:
                            res.data.sort((a, b) =>
                                new Date(a.created_at) < new Date(b.created_at) ? 1 : -1,
                            ) || [],
                        fetchState: 'success',
                    });
                    this.fetchSa(res.data[0].conversationid);
                } else {
                    this.setState({
                        fetchState: 'success',
                    });
                }
            })
            .catch((e) => {
                console.error(e);
                this.setState({
                    fetchState: 'error',
                });
                alert('Failed to fetch latest details of the patient [3]');
            });
    };

    fetchSa = (saId: string) => {
        this.setState({
            previewSaFetchState: 'loading',
            selectedSaId: saId,
        });
        fetchPastAssessmentSummary(saId)
            .then((res) => {
                this.setState({
                    previewSaFetchState: 'success',
                    assessmentSummary: res.response,
                });
            })
            .catch(() => {
                this.setState({
                    previewSaFetchState: 'error',
                });
            });
    };

    render(): ReactNode {
        const {
            saList,
            fetchState,
            assessmentSummary,
            previewSaFetchState,
            selectedSaId,
        } = this.state;

        if (fetchState === 'loading') {
            return (
                <div className="flex flex-col col-span-10 items-center justify-center w-full h-full space-y-64">
                    <div className="pulse">
                        <svg width="16px" height="12px">
                            <polyline id="back" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                            <polyline id="front" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                        </svg>
                    </div>
                    <p className="text-24 text-neutral-black">Fetching Past Assessments...</p>
                </div>
            );
        }

        return (
            <div className="flex col-span-10 overflow-hidden bg-white w-full">
                <div className="w-30p">
                    <div className="flex flex-col  h-full w-full bg-neutral-9 relative fade-visits">
                        <div className="absolute inset-0 overflow-auto py-16">
                            <div className="space-y-24 p-16 ">
                                {saList.length === 0 && (
                                    <div className="space-y-16">
                                        <p className="leading-5 text-12 text-neutral-5 font-700">
                                            {moment().format('MMMM YYYY')}
                                        </p>
                                        <div className="flex flex-col space-y-16">
                                            {Array.from(Array(2)).map((i) => (
                                                <div
                                                    key={i}
                                                    className="flex items-center w-full space-x-16"
                                                >
                                                    <div className="w-20 h-32 text-center font-700 bg-neutral-7"></div>
                                                    <div className="flex items-center justify-between h-32 p-8 leading-6 bg-neutral-7 w-87p rounded-32"></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {saList?.map((item, i) => (
                                    <div key={item.conversationid} className="space-y-16">
                                        {(i === 0 ||
                                            moment(item.created_at).format('MMMM YYYY') !==
                                                moment(saList[i - 1].created_at).format(
                                                    'MMMM YYYY',
                                                )) && (
                                            <p className="leading-5 text-12 text-neutral-5 font-700">
                                                {moment(item.created_at).format('MMMM YYYY')}
                                            </p>
                                        )}
                                        <div className="flex flex-col space-y-16">
                                            <div
                                                onClick={() => this.fetchSa(item.conversationid)}
                                                className="flex items-center space-x-16 cursor-pointer w-full"
                                            >
                                                <div
                                                    className={
                                                        selectedSaId === item.conversationid
                                                            ? 'flex items-center justify-between w-full p-8 leading-6 bg-white rounded-32 shadow-tight-3 transform-gpu scale-103'
                                                            : 'flex items-center justify-between w-full p-8 leading-6 bg-white rounded-32 patient-details-visit-shadow hover:shadow-tight-3 hover:scale-103 transition-all transform-gpu duration-300 ease-in-out'
                                                    }
                                                >
                                                    <div className="hidden lg:flex items-center justify-center w-28 h-28 rounded-full">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 384 512"
                                                            className={`w-16 h-16 fill-actionPrimary`}
                                                        >
                                                            <path d="M336 64h-80c0-35.3-28.7-64-64-64s-64 28.7-64 64H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM96 424c-13.3 0-24-10.7-24-24s10.7-24 24-24 24 10.7 24 24-10.7 24-24 24zm0-96c-13.3 0-24-10.7-24-24s10.7-24 24-24 24 10.7 24 24-10.7 24-24 24zm0-96c-13.3 0-24-10.7-24-24s10.7-24 24-24 24 10.7 24 24-10.7 24-24 24zm96-192c13.3 0 24 10.7 24 24s-10.7 24-24 24-24-10.7-24-24 10.7-24 24-24zm128 368c0 4.4-3.6 8-8 8H168c-4.4 0-8-3.6-8-8v-16c0-4.4 3.6-8 8-8h144c4.4 0 8 3.6 8 8v16zm0-96c0 4.4-3.6 8-8 8H168c-4.4 0-8-3.6-8-8v-16c0-4.4 3.6-8 8-8h144c4.4 0 8 3.6 8 8v16zm0-96c0 4.4-3.6 8-8 8H168c-4.4 0-8-3.6-8-8v-16c0-4.4 3.6-8 8-8h144c4.4 0 8 3.6 8 8v16z" />
                                                        </svg>
                                                    </div>
                                                    <div
                                                        className={
                                                            'flex items-center flex-1 capitalize'
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                selectedSaId === item.conversationid
                                                                    ? 'ml-8 text-13 text-neutral-1 font-700 w-85p'
                                                                    : 'ml-8 text-13 text-neutral-5 w-85p'
                                                            }
                                                        >
                                                            {moment(item.created_at).format(
                                                                'DD MMM YY',
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <svg
                                                            width="25"
                                                            height="25"
                                                            viewBox="0 0 28 28"
                                                            className={
                                                                selectedSaId !== item.conversationid
                                                                    ? 'fill-neutral-1 cursor-pointer fill-neutral-1'
                                                                    : 'fill-neutral-5 cursor-pointer fill-neutral-5'
                                                            }
                                                            preserveAspectRatio="xMidYMid meet"
                                                        >
                                                            <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"></path>
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                {saList.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center h-full space-y-16 select-none">
                        <div>
                            <svg
                                viewBox="0 0 448 512"
                                width="42"
                                height="48"
                                className="fill-neutral-3"
                            >
                                <path d="M400 64h-48V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H128V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM48 96h352c8.8 0 16 7.2 16 16v48H32v-48c0-8.8 7.2-16 16-16zm352 384H48c-8.8 0-16-7.2-16-16V192h384v272c0 8.8-7.2 16-16 16zm-105.3-95.9c4.7 4.7 4.7 12.3 0 17l-5.7 5.7c-4.7 4.7-12.3 4.7-17 0l-48-48.2-48.1 48.1c-4.7 4.7-12.3 4.7-17 0l-5.7-5.7c-4.7-4.7-4.7-12.3 0-17l48.1-48.1-48.1-48.1c-4.7-4.7-4.7-12.3 0-17l5.7-5.7c4.7-4.7 12.3-4.7 17 0l48.1 48.1 48.1-48.1c4.7-4.7 12.3-4.7 17 0l5.7 5.7c4.7 4.7 4.7 12.3 0 17L246.6 336l48.1 48.1z"></path>
                            </svg>
                        </div>
                        <div>
                            <p className="text-neutral-3">No Assessments from this patient yet</p>
                        </div>
                    </div>
                ) : previewSaFetchState === 'loading' ? (
                    <div className="flex flex-col items-center justify-center flex-1">
                        <div className="pulse">
                            <svg width="16px" height="12px">
                                <polyline id="back" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                                <polyline
                                    id="front"
                                    points="1 6 4 6 6 11 10 1 12 6 15 6"
                                ></polyline>
                            </svg>
                        </div>
                    </div>
                ) : previewSaFetchState === 'success' ? (
                    <div className="flex flex-col bg-white w-70p patient-details-viewport-shadow h-full overflow-hidden">
                        <div className="flex flex-col h-full overflow-hidden">
                            <div className="flex flex-col flex-1 overflow-hidden">
                                {assessmentSummary && (
                                    <AssessmentSummary assessmentSummary={assessmentSummary} />
                                )}
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }
}

export default connect()(SelfAssessmentRightPanel);
